import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Grid,
  Divider,
  Button,
  Alert,
  keyframes,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { fetchCheckoutDetails } from "../redux/actions/checkoutActions";
import PublicStkPushModal from "../components/checkout/PublicStkPushModal";

// --- ANIMATIONS ---
const pulseAnimation = keyframes`
  0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(255, 152, 0, 0.7); }
  70% { transform: scale(1); box-shadow: 0 0 0 15px rgba(255, 152, 0, 0); }
  100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(255, 152, 0, 0); }
`;

const CheckoutPage = () => {
  const { token } = useParams(); // Using useParams to match your AppRoutes setup
  const dispatch = useDispatch();

  const { loadingCheckout, checkoutData, errorCheckout } = useSelector(
    (state) => state.checkout,
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPolling, setIsPolling] = useState(false);

  // 1. Initial Load
  useEffect(() => {
    if (token) {
      dispatch(fetchCheckoutDetails(token, false)); // Non-silent load
    }
  }, [dispatch, token]);

  // 2. Polling Logic
  useEffect(() => {
    let pollInterval;
    let timeoutId;

    if (isPolling && checkoutData?.status !== "PAID") {
      // Poll every 3 seconds SILENTLY
      pollInterval = setInterval(() => {
        dispatch(fetchCheckoutDetails(token, true));
      }, 3000);

      // Timeout after 45 seconds to stop infinite polling
      timeoutId = setTimeout(() => {
        clearInterval(pollInterval);
        setIsPolling(false);
      }, 45000);
    }

    // Stop polling immediately if status becomes PAID
    if (checkoutData?.status === "PAID" && isPolling) {
      clearInterval(pollInterval);
      clearTimeout(timeoutId);
      setIsPolling(false);
    }

    return () => {
      clearInterval(pollInterval);
      clearTimeout(timeoutId);
    };
  }, [isPolling, checkoutData?.status, dispatch, token]);

  // Handlers
  const handleStkInitiated = () => {
    setIsPolling(true);
  };

  if (!token)
    return (
      <Container maxWidth="sm" sx={{ py: 10 }}>
        <Alert severity="error">Invalid checkout token.</Alert>
      </Container>
    );
  if (loadingCheckout)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 15 }}>
        <CircularProgress />
      </Box>
    );
  if (errorCheckout)
    return (
      <Container maxWidth="sm" sx={{ py: 10 }}>
        <Alert severity="error">{errorCheckout}</Alert>
      </Container>
    );
  if (!checkoutData) return null;

  const {
    invoiceNumber,
    dueDate,
    header,
    customerBusinessName,
    balanceDue,
    description,
    status,
  } = checkoutData;
  const company = header?.companySnapshot;
  const isPaid = status === "PAID";

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper
        elevation={6}
        sx={{
          p: { xs: 3, md: 5 },
          borderRadius: 3,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* BIG PAID WATERMARK (Visible only if paid) */}
        {isPaid && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%) rotate(-30deg)",
              opacity: 0.08,
              pointerEvents: "none",
              zIndex: 0,
            }}
          >
            <Typography
              variant="h1"
              sx={{ fontSize: "12rem", fontWeight: 900, color: "success.main" }}
            >
              PAID
            </Typography>
          </Box>
        )}

        {/* HEADER SECTION */}
        <Grid
          container
          spacing={3}
          justifyContent="space-between"
          sx={{ position: "relative", zIndex: 1 }}
        >
          <Grid item xs={12} sm={6}>
            <Typography variant="h4" fontWeight="bold" color="primary">
              {header?.templateMetadata?.labels?.invoiceTitle || "INVOICE"}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" mt={1}>
              #{invoiceNumber}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} sx={{ textAlign: { sm: "right" } }}>
            <Typography variant="h6" fontWeight="bold">
              {company?.companyName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {company?.address} <br /> {company?.email} <br /> {company?.phone}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        {/* BILLING INFO */}
        <Grid container spacing={3} sx={{ position: "relative", zIndex: 1 }}>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              textTransform="uppercase"
            >
              Billed To
            </Typography>
            <Typography variant="body1" fontWeight="bold" mt={1}>
              {customerBusinessName}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              Description: {description}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} sx={{ textAlign: { sm: "right" } }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              textTransform="uppercase"
            >
              Due Date
            </Typography>
            <Typography variant="body1" fontWeight="bold" mt={1}>
              {new Date(dueDate).toLocaleDateString()}
            </Typography>
          </Grid>
        </Grid>

        {/* SUMMARY AMOUNT SECTION */}
        <Box
          sx={{
            mt: 5,
            p: 3,
            bgcolor: isPaid ? "success.light" : "#f8f9fa",
            borderRadius: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Typography variant="h6" color={isPaid ? "white" : "text.primary"}>
            {isPaid ? "Total Paid" : "Total Balance Due"}
          </Typography>
          <Typography
            variant="h4"
            fontWeight="bold"
            color={isPaid ? "white" : "error.main"}
          >
            KES{" "}
            {isPaid
              ? checkoutData.amountPaid.toFixed(2)
              : balanceDue.toFixed(2)}
          </Typography>
        </Box>

        {/* ACTIONS / STATUS INDICATORS */}
        <Box
          sx={{ mt: 5, textAlign: "center", position: "relative", zIndex: 1 }}
        >
          {isPaid ? (
            <Alert
              icon={<CheckCircleIcon fontSize="inherit" />}
              severity="success"
              sx={{ justifyContent: "center", py: 2, fontSize: "1.1rem" }}
            >
              This invoice is fully paid. Thank you!
            </Alert>
          ) : isPolling ? (
            // POLLING HEARTBEAT UI
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                p: 3,
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  bgcolor: "warning.main",
                  mb: 3,
                  animation: `${pulseAnimation} 1.5s infinite`,
                }}
              />
              <Typography variant="h6" color="warning.main" fontWeight="bold">
                Awaiting Payment Confirmation...
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={1}>
                Please complete the M-Pesa prompt on your phone. <br /> Do not
                close this page.
              </Typography>
            </Box>
          ) : (
            // NORMAL PAY BUTTON
            <Button
              variant="contained"
              color="success"
              size="large"
              sx={{ px: 5, py: 1.5, fontSize: "1.1rem" }}
              onClick={() => setIsModalOpen(true)}
            >
              Pay via M-Pesa STK
            </Button>
          )}

          {/* Time out message if polling stopped but not paid */}
          {!isPaid &&
            !isPolling &&
            balanceDue > 0 &&
            checkoutData?.status === "SENT" && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mt={2}
              >
                If your prompt timed out or failed, you can try again.
              </Typography>
            )}
        </Box>

        {/* FOOTER */}
        {header?.templateMetadata?.footer?.termsAndConditions && (
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            mt={5}
            textAlign="center"
          >
            {header.templateMetadata.footer.termsAndConditions}
          </Typography>
        )}
      </Paper>

      {/* RENDER MODAL */}
      <PublicStkPushModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        token={token}
        balanceDue={balanceDue}
        defaultPhone={checkoutData?.customerPhone}
        onSuccess={handleStkInitiated}
      />
    </Container>
  );
};

export default CheckoutPage;
